/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import React, { lazy, Suspense, useCallback, useMemo, useSyncExternalStore } from 'react';

import { keyframes } from '@compiled/react';
import { ErrorBoundary } from 'react-error-boundary';
import { type useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { shallowEqualObjects } from 'shallow-equal';

import { type DocNode } from '@atlaskit/adf-schema';
import { cssMap, cx, jsx } from '@atlaskit/css';
import { ComposableEditor, type EditorNextProps } from '@atlaskit/editor-core/composable-editor';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';
import UFOSuspense from '@atlaskit/react-ufo/suspense';
import { token } from '@atlaskit/tokens';
import {
	type AutoSuggestModule,
	importAutoSuggest,
} from '@atlassian/conversation-assistant-auto-suggest/import-auto-suggest';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	ExperienceFailure,
	ExperienceStart,
	ExperienceSuccess,
} from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import { FileUploadDropzone } from '@atlassian/conversation-assistant-ui-components/ui/FileUploadDropzone';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { type useConversationFiles } from '../../common/controllers/conversation-files';
import { MAX_FILES } from '../../common/controllers/conversation-files/constants';
import { ChatInputActionButtons } from '../../common/ui/chat-input-action-buttons';
import { type usePageContextType } from '../../common/ui/chat-input-action-buttons/current-page-as-context/usePageContextType';
import { ComposableEditorWithSkills } from '../../common/ui/composable-editor';
import { InputContextListDisplay } from '../../common/ui/input-context-list-display';
import { NegativeCharacterCount } from '../../common/ui/negative-character-count';
import DisabledVoiceFooterRow from '../../common/ui/voice-enabled-footer-row/disabled-fallback';
import { type ChatMode, type ReasoningMode } from '../../controllers/chat-mode-store/types';
import { type useChatModeStoreActions } from '../../controllers/chat-mode-store/useChatModeStoreActions';
import { useSkillTagExtensionProvider } from '../skills-editor-extension/useSkillTagExtensionProvider';

import { AgentInputHat } from './AgentInputHat';
import { FilteredAppsTag } from './chat-mode-menu/filtered-apps';
import { messages as filteredAppsMessages } from './chat-mode-menu/filtered-apps/messages';
import { PillsWrapper } from './chat-mode-menu/pills-wrapper';
import { ReasoningModeMenu } from './chat-mode-menu/ReasoningModeMenu';
import { reasoningModeMenuMessages } from './chat-mode-menu/reasoningModeMenuMessages';
import { ControlPanel } from './control-panel';
import { type ControlPanelProps } from './control-panel/types';
import { DeepResearchTag } from './deep-research-tag/DeepResearchTag';
import { messages as deepResearchTagMessages } from './deep-research-tag/messages';
import {
	DictationStatusDisplay,
	isDictationStatusVisible,
	useDictationStatus,
} from './DictationStatusDisplay';
import { type MentionAgentsSpotlightPostOfficeMessage } from './MentionAgentsSpotlightPostOfficeMessage';
import { TaskModePromptAnimation } from './TaskModePromptAnimation';
import { type AutoSuggestConfig, type ChatInputInternalProps } from './types';
import { type EditorApi, type EditorStore, useEditorStore } from './useEditorStore';

const DEFAULT_INPUT_HEIGHT_ROVO_CHAT = 56;
const DEFAULT_INPUT_HEIGHT_STAGING_AREA = 48;
const SINGLE_DEFAULT_INPUT_HEIGHT_ROVO_CHAT = 24;
const WRAPPER_BORDER_RADIUS = token('radius.xlarge');

const fadeIn = keyframes({
	from: {
		opacity: 0,
	},
	to: {
		opacity: 1,
	},
});

const VoiceEnabledFooterRow = lazy(
	() =>
		import(
			/* webpackChunkName: "ai-audio-voice-dictation" */ '../../common/ui/voice-enabled-footer-row'
		),
);

const styles = cssMap({
	/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
	wrapper: {
		minHeight: '100px',
		position: 'relative',
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: WRAPPER_BORDER_RADIUS,
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		// @ts-expect-error - raw boxShadow / ProseMirror nested selector not supported in cssMap strict types; required by editor integration
		boxShadow: '0 -2px 50px 8px rgba(30, 31, 33, 0.08)',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& .ak-editor-content-area': {
			paddingTop: token('space.075'),
			paddingRight: token('space.075'),
			paddingLeft: token('space.075'),
		},
	},
	/* eslint-enable @atlaskit/design-system/ensure-design-token-usage */
	inputHatHeader: {
		borderStartStartRadius: WRAPPER_BORDER_RADIUS,
		borderStartEndRadius: WRAPPER_BORDER_RADIUS,
		backgroundColor: token('elevation.surface.sunken'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
	},
	wrapperContent: {
		paddingInline: token('space.150'),
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
	},
	deepResearchTagWrapper: {
		paddingInlineStart: token('space.050'),
	},
	wrapperBackgroundDefault: {
		backgroundColor: 'transparent',
	},
	wrapperBackgroundOpaque: {
		backgroundColor: token('color.background.input'),
	},
	wrapperWithTruncation: {
		contain: 'inline-size',
		// @ts-expect-error - nested selector '& .ProseMirror' not supported in cssMap strict types; required by editor integration
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& .ProseMirror': {
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			display: '-webkit-box',
			WebkitLineClamp: 2,
			WebkitBoxOrient: 'vertical',
			maxHeight: '48px',
			animation: `${fadeIn} 0.3s ease-in forwards`,
		},
	},
	wrapperWithTruncationSingleLine: {
		contain: 'inline-size',
		// @ts-expect-error - nested selector '& .ProseMirror' not supported in cssMap strict types; required by editor integration
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& .ProseMirror': {
			overflow: 'clip',
			textOverflow: 'ellipsis',
			display: '-webkit-box',
			WebkitLineClamp: 1,
			WebkitBoxOrient: 'vertical',
			maxHeight: '1.5em',
			animation: `${fadeIn} 0.3s ease-in forwards`,
		},
	},
	wrapperWithDropdown: {
		borderEndStartRadius: 0,
		borderEndEndRadius: 0,
		minHeight: '104px',
	},
	actionRow: {
		paddingBlockStart: token('space.050'),
	},
	editorWrapperWithSpacing: {
		marginBlockEnd: token('space.200'),
	},
	editorStagingAreaMinHeight: {
		// @ts-expect-error - nested selector '& .ProseMirror' not supported in cssMap strict types; required by editor integration
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& .ProseMirror': { minHeight: `${DEFAULT_INPUT_HEIGHT_STAGING_AREA}px` },
	},
	editorRovoChatMinHeight: {
		// @ts-expect-error - nested selector '& .ProseMirror' not supported in cssMap strict types; required by editor integration
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& .ProseMirror': { minHeight: `${DEFAULT_INPUT_HEIGHT_ROVO_CHAT}px` },
	},
	editorSingleLineHeight: {
		flexGrow: 1,
		minHeight: '38px',
		// @ts-expect-error - nested selector '& .ProseMirror' not supported in cssMap strict types; required by editor integration
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& .ProseMirror': { minHeight: `${SINGLE_DEFAULT_INPUT_HEIGHT_ROVO_CHAT}px` },
	},
	fileDragging: {
		backgroundColor: token('color.background.accent.blue.subtlest'),
		border: 'none',
	},
	// Applied only when dictation covers an empty editor so the status can fill
	// that editor area without affecting the layout used for existing content.
	dictationOverlayContainer: {
		position: 'relative',
	},
	wrapperBlankObject: {
		border: 'none',
		// @ts-expect-error - raw boxShadow / ProseMirror nested selector not supported in cssMap strict types; required by editor integration
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		boxShadow: '0 0 1px 0 rgba(30, 31, 33, 0.31), 0 4px 12px 0 rgba(30, 31, 33, 0.15)',
	},
});

export type AutoSuggestOption = {
	text: string;
	adf?: DocNode;
};

const AutoSuggest = lazy(async () => {
	const autoSuggestModule: AutoSuggestModule = await importAutoSuggest();
	return { default: autoSuggestModule.AutoSuggest };
});

type ChatInputInnerContentProps = Pick<
	ChatInputInternalProps,
	| 'actionsMenuItemsVisibility'
	| 'assistanceServiceParams'
	| 'browserContextSanitizer'
	| 'contentContext'
	| 'contextProviders'
	| 'customSlotAboveActionRow'
	| 'customSlotBeforeSubmitButton'
	| 'dropdownSlot'
	| 'enableAppsDiscoveryUi'
	| 'enableMaxMode'
	| 'enableBrowserContextBanner'
	| 'enableSkills'
	| 'enableSkillsInStagingArea'
	| 'enableSkillsInSpecialistPromptInput'
	| 'entryPoint'
	| 'mode'
	| 'fileUploadAssistanceOverrides'
	| 'forceTruncatedWrapperOnEmptyEditor'
	| 'hoveredPrompt'
	| 'isAgentTyping'
	| 'isBackgroundTransparent'
	| 'isDisabled'
	| 'menuPosition'
	| 'onAbort'
	| 'onBrowsePromptLibraryClick'
	| 'onContextAvailable'
	| 'onContextBrowserShow'
	| 'onCreateSpaceClick'
	| 'onDiscoverSkillsClick'
	| 'onFilteredAppsChange'
	| 'onOrgKnowledgeToggle'
	| 'onReasoningModeChange'
	| 'onWebSearchToggle'
	| 'productKey'
	| 'promptDraftAndModeKey'
	| 'selectedAgentForInputHat'
	| 'shouldRenderChatModeMenusInPortal'
	| 'shouldRenderQuickActionsInPortal'
	| 'showChatModeMenu'
	| 'showActionsButton'
	| 'showSingleLineEditor'
	| 'siteId'
	| 'topAccessory'
	| 'urlValidationConfig'
	| 'useCurrentPageContext'
	| 'dictationOverlayBackground'
> & {
	autoSuggestADF: DocNode | null | undefined;
	autoSuggestConfig: AutoSuggestConfig | undefined;
	autoSuggestQuery: string;
	blankObjectStyles: boolean | undefined;
	charactersOverLimit: number;
	chatModeReasoningMode: ChatMode['reasoningMode'];
	chatModeDeepResearchEnabled: boolean;
	chatModeTaskModeEnabled: boolean;
	customSlotBeforeChatModeMenu?: React.ReactNode;
	closeEditorToolbar: () => void;
	closeTypeAhead: () => void;
	// Gate-off keeps the pre-stabilization direct editor props, while gate-on
	// passes only the store so editor churn stays inside ChatInputEditor.
	editorApi?: EditorApi;
	editorProps?: EditorNextProps;
	editorStore?: EditorStore;
	files: ReturnType<typeof useConversationFiles>['files'];
	formatMessage: ReturnType<typeof useIntl>['formatMessage'];
	getChatMode: ReturnType<typeof useChatModeStoreActions>['getChatMode'];
	handleDeepResearchClick: () => void;
	handleEditorBlur: () => void;
	handleFilesSelected: ReturnType<typeof useConversationFiles>['handleFilesSelected'];
	handleSuggestionSelect: (suggestion: AutoSuggestOption) => void;
	handleUrlsSelected: (urls: string[]) => void;
	hasActiveAgentMention: boolean;
	hasCodeBlock: boolean;
	inputValueLength: number;
	isReasoningModeDisabledByDeepResearch: boolean;
	isSendDisabled: boolean;
	openAddTypeAhead: () => void;
	openLinkTypeAheadFromMenu: () => void;
	openMentionTypeAhead: () => void;
	onInsertTranscript: (transcript: string) => void | Promise<void>;
	onMicPermissionDenied?: () => void;
	pageContextType: ReturnType<typeof usePageContextType>;
	promptHasSkill: boolean;
	renderAutoApplyToggle: (() => React.ReactNode) | undefined;
	submitButtonOnSave: () => void;
	setDeepResearchEnabled: ReturnType<typeof useChatModeStoreActions>['setDeepResearchEnabled'];
	setReasoningMode: ReturnType<typeof useChatModeStoreActions>['setReasoningMode'];
	setTaskModeAnimationTrigger: React.Dispatch<React.SetStateAction<number>>;
	onReasoningModeMenuOpenChange?: (isOpen: boolean) => void;
	shouldKeepTaskModePromptMounted: boolean;
	setWebSearchEnabled: ReturnType<typeof useChatModeStoreActions>['setWebSearchEnabled'];
	shouldRenderInputAgentHat: boolean;
	selectedAgentInputHatImageUrl: string | undefined;
	handleSelectedAgentDismiss: () => void;
	/**
	 * True when a specialist agent owns the next turn (the conversation is agentic
	 * and Rovo is not the active @-mention). Drives disabling/hiding of the
	 * Rovo-owned controls (reasoning mode, web search, app filters) and the skills
	 * editor. The parent derives it as `isConversationAgentic && !isRovoMentioned`.
	 */
	isSpecialistPromptInput: boolean;
	/**
	 * Capability flag (`isConversationAgentic && enableAgentMentions`): an agentic
	 * conversation where the user can switch to Rovo via @-mention. When true, the
	 * Rovo-owned controls render **disabled with a tooltip** rather than hidden;
	 * when false, an agentic conversation hides them (pre-change behaviour) since a
	 * control the user can never reach would be meaningless.
	 *
	 * `enableAgentMentions` is per-integration-point, so agents may be absent even with the experiment on.
	 * The parent passes the effective capability; Agent Studio / rovo-playground
	 * omit the integration opt-in.
	 * This flag only selects show-disabled vs hide;
	 * actual disabling comes from `isSpecialistPromptInput` / `isAgentTyping` / `isDisabled`.
	 */
	canSwitchBackToRovo: boolean;
	showSourceModeToggle: boolean;
	showAgentSourceControlSpotlight?: boolean;
	onDismissAgentSourceControlSpotlight?: (source?: 'cross-icon' | 'understood-button') => void;
	sourceControlPostOfficeMessage?: MentionAgentsSpotlightPostOfficeMessage;
	shouldRenderVoiceEnabledFooterRow: boolean;
	shouldShowDeepResearchInAddMenu: boolean;
	stableFileInputChange: boolean;
	SubmitButton: ChatInputInternalProps['submitButton'];
	submitButtonTooltip: ControlPanelProps['submitButtonTooltip'];
	taskModeAnimationTrigger: number;
	tooltipOptions: ControlPanelProps['tooltipOptions'];
	webSearchConfigEnabled: boolean;
};

type ChatInputEditorProps = {
	contentContext: ChatInputInternalProps['contentContext'];
	editorApi: EditorApi;
	editorProps: EditorNextProps;
	enableSkills: ChatInputInternalProps['enableSkills'];
	enableSkillsInStagingArea: ChatInputInternalProps['enableSkillsInStagingArea'];
	enableSkillsInSpecialistPromptInput: ChatInputInternalProps['enableSkillsInSpecialistPromptInput'];
	isSpecialistPromptInput: boolean;
	promptHasSkill: boolean;
	siteId: ChatInputInternalProps['siteId'];
};

const ChatInputEditor = ({
	contentContext,
	editorApi,
	editorProps,
	enableSkills,
	enableSkillsInStagingArea,
	enableSkillsInSpecialistPromptInput,
	isSpecialistPromptInput,
	promptHasSkill,
	siteId,
}: ChatInputEditorProps): React.JSX.Element => {
	const resolvedIsSpecialistPromptInput =
		isSpecialistPromptInput && !enableSkillsInSpecialistPromptInput;
	const resolvedEnableSkills =
		!!enableSkills && (contentContext !== 'staging-area' || !!enableSkillsInStagingArea);
	const skillTagFallbackProvider = useSkillTagExtensionProvider(siteId);
	const fallbackEditor = (
		<ComposableEditor
			extensionProviders={resolvedEnableSkills ? skillTagFallbackProvider : undefined}
			{...editorProps}
		/>
	);

	return (
		<UFOSegment name="composable-editor">
			<ErrorBoundary
				fallbackRender={({ error }) => (
					<>
						<ExperienceFailure error={error} name={ExperienceName.LOAD_PANEL_COMPOSABLE_EDITOR} />
						<ExperienceFailure error={error} name={ExperienceName.LOAD_CHAT_PROMPT_INPUT_SKILLS} />
						{fallbackEditor}
					</>
				)}
				onError={(error) => {
					logException({
						exception: error,
						name: ExperienceName.LOAD_PANEL_COMPOSABLE_EDITOR,
						tags: {
							siteId: siteId,
						},
					});
				}}
			>
				<ExperienceStart name={ExperienceName.LOAD_PANEL_COMPOSABLE_EDITOR} />
				<ExperienceStart name={ExperienceName.LOAD_CHAT_PROMPT_INPUT_SKILLS} />
				<AtlassianRelayEnvironmentProvider>
					<UFOSegment name="chat-prompt-input-skills">
						<Suspense fallback={fallbackEditor}>
							<ComposableEditorWithSkills
								editorApi={editorApi}
								isSpecialistPromptInput={resolvedIsSpecialistPromptInput}
								promptHasSkill={promptHasSkill}
								enableSkills={resolvedEnableSkills}
								siteId={siteId}
								{...editorProps}
							/>
							<ExperienceSuccess name={ExperienceName.LOAD_CHAT_PROMPT_INPUT_SKILLS} />
						</Suspense>
					</UFOSegment>
				</AtlassianRelayEnvironmentProvider>
				<ExperienceSuccess name={ExperienceName.LOAD_PANEL_COMPOSABLE_EDITOR} />
			</ErrorBoundary>
		</UFOSegment>
	);
};

type StoreBackedChatInputEditorProps = Omit<ChatInputEditorProps, 'editorApi' | 'editorProps'> & {
	editorStore: EditorStore;
};

const StoreBackedChatInputEditor = ({
	editorStore,
	...editorContainerProps
}: StoreBackedChatInputEditorProps): React.JSX.Element => {
	const { editorApi, editorProps: currentEditorProps } = useSyncExternalStore(
		editorStore.subscribe,
		editorStore.getSnapshot,
		editorStore.getSnapshot,
	);

	return (
		<ChatInputEditor
			{...editorContainerProps}
			editorApi={editorApi}
			editorProps={currentEditorProps}
		/>
	);
};

export type ChatInputInnerProps = Omit<
	ChatInputInnerContentProps,
	'editorApi' | 'editorProps' | 'editorStore'
> & {
	editorApi: EditorApi;
	editorProps: EditorNextProps;
	isRenderStabilizationEnabled: boolean;
};

const ChatInputInnerContent = (props: ChatInputInnerContentProps): React.JSX.Element => {
	di(ComposableEditor, ComposableEditorWithSkills, AtlassianRelayEnvironmentProvider);

	const {
		files,
		handleFilesSelected,
		handleUrlsSelected,
		urlValidationConfig,
		chatModeReasoningMode,
		chatModeDeepResearchEnabled,
		chatModeTaskModeEnabled,
		taskModeAnimationTrigger,
		isBackgroundTransparent,
		dropdownSlot,
		isAgentTyping,
		showSingleLineEditor,
		hoveredPrompt,
		forceTruncatedWrapperOnEmptyEditor,
		inputValueLength,
		blankObjectStyles,
		dictationOverlayBackground,
		selectedAgentForInputHat,
		shouldRenderInputAgentHat,
		selectedAgentInputHatImageUrl,
		handleSelectedAgentDismiss,
		siteId,
		topAccessory,
		shouldShowDeepResearchInAddMenu,
		isSpecialistPromptInput,
		canSwitchBackToRovo,
		showAgentSourceControlSpotlight,
		onDismissAgentSourceControlSpotlight,
		sourceControlPostOfficeMessage,
		setDeepResearchEnabled,
		promptDraftAndModeKey,
		onReasoningModeChange,
		isDisabled,
		handleEditorBlur,
		hasCodeBlock,
		contentContext,
		editorApi,
		editorProps,
		editorStore,
		promptHasSkill,
		enableSkills,
		enableSkillsInStagingArea,
		enableSkillsInSpecialistPromptInput,
		charactersOverLimit,
		customSlotAboveActionRow,
		customSlotBeforeChatModeMenu,
		showChatModeMenu,
		showActionsButton,
		actionsMenuItemsVisibility,
		entryPoint,
		mode,
		menuPosition,
		openLinkTypeAheadFromMenu,
		openMentionTypeAhead,
		openAddTypeAhead,
		onDiscoverSkillsClick,
		handleDeepResearchClick,
		onBrowsePromptLibraryClick,
		onCreateSpaceClick,
		hasActiveAgentMention,
		assistanceServiceParams,
		fileUploadAssistanceOverrides,
		contextProviders,
		onContextBrowserShow,
		onContextAvailable,
		useCurrentPageContext = false,
		closeTypeAhead,
		closeEditorToolbar,
		shouldRenderQuickActionsInPortal,
		enableBrowserContextBanner,
		browserContextSanitizer,
		shouldRenderChatModeMenusInPortal,
		webSearchConfigEnabled,
		setWebSearchEnabled,
		onWebSearchToggle,
		onOrgKnowledgeToggle,
		onFilteredAppsChange,
		onInsertTranscript,
		onMicPermissionDenied,
		setReasoningMode,
		enableAppsDiscoveryUi,
		enableMaxMode,
		setTaskModeAnimationTrigger,
		onReasoningModeMenuOpenChange,
		shouldKeepTaskModePromptMounted,
		isReasoningModeDisabledByDeepResearch,
		stableFileInputChange,
		formatMessage,
		customSlotBeforeSubmitButton,
		submitButtonOnSave,
		SubmitButton,
		isSendDisabled,
		submitButtonTooltip,
		tooltipOptions,
		onAbort,
		getChatMode,
		shouldRenderVoiceEnabledFooterRow,
		productKey,
		autoSuggestConfig,
		autoSuggestQuery,
		autoSuggestADF,
		handleSuggestionSelect,
		showSourceModeToggle,
		pageContextType,
		renderAutoApplyToggle,
	} = props;
	const { sendAnalyticsEvent } = useAnalytics();

	// Stable callback shared by the footer controls so the
	// outer `React.memo` boundary is not defeated by a new function identity on
	// every render.
	const chatModeStateForReasoningModeMenu = useMemo(
		() => ({
			reasoningMode: chatModeReasoningMode,
		}),
		[chatModeReasoningMode],
	);

	const { dictationStatus, handleDictationStatusChange } = useDictationStatus();
	const shouldOverlayDictationStatus =
		inputValueLength === 0 && isDictationStatusVisible(dictationStatus);

	const handleMenuOpen = useCallback(() => {
		closeTypeAhead();
		closeEditorToolbar();
	}, [closeTypeAhead, closeEditorToolbar]);

	// Show Rovo-owned controls disabled (informative) when switch-back is reachable;
	// otherwise hide them entirely. See the `canSwitchBackToRovo` prop doc above.
	const showDisabledRovoControlsForAgent = canSwitchBackToRovo;
	const hideRovoControlsForAgent = isSpecialistPromptInput && !canSwitchBackToRovo;

	// Named per-control tooltips shown when the Rovo-only controls are disabled
	// because the user is chatting with an agent. Each names its own control so
	// the reason is clear to sighted and screen-reader users alike.
	const reasoningModeAgentDisabledTooltip = formatMessage(
		reasoningModeMenuMessages.agentDisabledTooltip,
	);
	const filteredAppsAgentDisabledTooltip = formatMessage(filteredAppsMessages.agentDisabledTooltip);
	const deepResearchAgentDisabledTooltip = formatMessage(
		deepResearchTagMessages.agentDisabledTooltip,
	);

	// Stable composing callbacks so a new function identity per render does not
	// defeat memo boundaries on footer controls.
	const handleDeepResearchToggle = useCallback(
		(enabled: boolean) => {
			const mode = enabled ? 'deep-research' : 'auto';
			setDeepResearchEnabled(promptDraftAndModeKey, enabled);
			onReasoningModeChange?.(mode);
		},
		[setDeepResearchEnabled, promptDraftAndModeKey, onReasoningModeChange],
	);
	const handleWebSearchToggle = useCallback(
		(enabled: boolean) => {
			setWebSearchEnabled(promptDraftAndModeKey, enabled);
			if (fg('rovo_chat_web_search_preference')) {
				sendAnalyticsEvent({
					eventType: 'ui',
					action: 'change',
					actionSubject: 'toggle',
					actionSubjectId: 'rovoChatWebSearchPromptPreference',
					source: 'rovoChatPromptInput',
					attributes: {
						enabled,
					},
				});
			}
			onWebSearchToggle?.(enabled);
		},
		[setWebSearchEnabled, promptDraftAndModeKey, sendAnalyticsEvent, onWebSearchToggle],
	);
	const handleDeepResearchDismiss = useCallback(() => {
		handleDeepResearchToggle(false);
	}, [handleDeepResearchToggle]);
	const handleFastReasoningModeChange = useCallback(
		(mode: ReasoningMode) => {
			if (mode === 'task' && !chatModeTaskModeEnabled && fg('rovo_chat_task_mode_design_refresh')) {
				setTaskModeAnimationTrigger((n) => n + 1);
			}
			setReasoningMode(promptDraftAndModeKey, mode);
			onReasoningModeChange?.(mode);
		},
		[
			chatModeTaskModeEnabled,
			setTaskModeAnimationTrigger,
			setReasoningMode,
			promptDraftAndModeKey,
			onReasoningModeChange,
		],
	);

	return (
		<UFOSegment name="chat-input-inner">
			<FileUploadDropzone
				maxFiles={MAX_FILES}
				currentFileCount={files.length}
				onFilesSelected={handleFilesSelected}
				onUrlsSelected={handleUrlsSelected}
				urlValidationConfig={urlValidationConfig}
			>
				{({ isDraggedOver, innerProps, dropzoneContent }) => (
					<TaskModePromptAnimation
						isTaskMode={
							!!(
								enableMaxMode &&
								chatModeTaskModeEnabled &&
								fg('rovo_chat_task_mode_design_refresh')
							)
						}
						shouldKeepPromptMounted={shouldKeepTaskModePromptMounted}
						triggerAnimation={taskModeAnimationTrigger}
					>
						<Stack
							ref={innerProps.ref}
							xcss={cx(
								styles.wrapper,
								isBackgroundTransparent &&
									!(
										enableMaxMode &&
										chatModeTaskModeEnabled &&
										fg('rovo_chat_task_mode_design_refresh')
									)
									? styles.wrapperBackgroundDefault
									: styles.wrapperBackgroundOpaque,
								!!dropdownSlot && styles.wrapperWithDropdown,
								isDraggedOver && styles.fileDragging,
								!showSingleLineEditor &&
									(!!hoveredPrompt ||
										(!!forceTruncatedWrapperOnEmptyEditor && inputValueLength === 0)) &&
									styles.wrapperWithTruncation,
								showSingleLineEditor &&
									(!!hoveredPrompt || inputValueLength === 0) &&
									styles.wrapperWithTruncationSingleLine,
								blankObjectStyles && styles.wrapperBlankObject,
							)}
							testId="chat-input-wrapper"
						>
							{selectedAgentForInputHat && shouldRenderInputAgentHat && (
								<Box xcss={styles.inputHatHeader}>
									<AgentInputHat
										agentId={selectedAgentForInputHat.id}
										agentName={selectedAgentForInputHat.name}
										agentNamedId={selectedAgentForInputHat.namedId}
										agentIdentityAccountId={selectedAgentForInputHat.identityAccountId}
										creatorType={selectedAgentForInputHat.creatorType}
										imageUrl={selectedAgentInputHatImageUrl}
										cloudId={siteId}
										onDismiss={handleSelectedAgentDismiss}
									/>
								</Box>
							)}
							<Stack xcss={styles.wrapperContent} testId="chat-input-wrapper-content">
								{topAccessory}
								{shouldShowDeepResearchInAddMenu &&
									chatModeDeepResearchEnabled &&
									!hideRovoControlsForAgent && (
										<Inline xcss={styles.deepResearchTagWrapper}>
											<DeepResearchTag
												onDismiss={handleDeepResearchDismiss}
												isDisabled={isDisabled || isAgentTyping || isSpecialistPromptInput}
												disabledTooltip={
													showDisabledRovoControlsForAgent
														? deepResearchAgentDisabledTooltip
														: undefined
												}
											/>
										</Inline>
									)}
								<Box
									onBlur={handleEditorBlur}
									onPaste={innerProps.onPaste}
									xcss={cx(
										shouldOverlayDictationStatus ? styles.dictationOverlayContainer : undefined,
										hasCodeBlock ? styles.editorWrapperWithSpacing : undefined,
										showSingleLineEditor
											? styles.editorSingleLineHeight
											: contentContext === 'staging-area' ||
												  contentContext === 'blank-object-experience'
												? styles.editorStagingAreaMinHeight
												: styles.editorRovoChatMinHeight,
									)}
								>
									{editorStore ? (
										<StoreBackedChatInputEditor
											contentContext={contentContext}
											editorStore={editorStore}
											enableSkills={enableSkills}
											enableSkillsInStagingArea={enableSkillsInStagingArea}
											enableSkillsInSpecialistPromptInput={enableSkillsInSpecialistPromptInput}
											isSpecialistPromptInput={isSpecialistPromptInput}
											promptHasSkill={promptHasSkill}
											siteId={siteId}
										/>
									) : (
										/*
										 * Gate-off keeps the pre-stabilization direct-prop path.
										 * Once `rovo_ttai_segment_render_stabilization` rolls out and
										 * this bridge is removed, editorApi/editorProps can be required
										 * again and these assertions should go away with the fallback.
										 */
										<ChatInputEditor
											contentContext={contentContext}
											editorApi={editorApi!}
											editorProps={editorProps!}
											enableSkills={enableSkills}
											enableSkillsInStagingArea={enableSkillsInStagingArea}
											enableSkillsInSpecialistPromptInput={enableSkillsInSpecialistPromptInput}
											isSpecialistPromptInput={isSpecialistPromptInput}
											promptHasSkill={promptHasSkill}
											siteId={siteId}
										/>
									)}

									<DictationStatusDisplay
										appearance={shouldOverlayDictationStatus ? 'overlay' : 'row'}
										status={dictationStatus}
										backgroundOverride={dictationOverlayBackground}
									/>
								</Box>
								<Inline spread="space-between">
									<Box />
									<NegativeCharacterCount count={-1 * charactersOverLimit} />
								</Inline>
								<InputContextListDisplay attachtmentsStoreKey={promptDraftAndModeKey} />
								{customSlotAboveActionRow}
								<Inline
									spread="space-between"
									xcss={styles.actionRow}
									testId="chat-prompt-footer-row"
								>
									{/*
										Voice dictation routing is lazy. The 3 slots
										below are passed into either VoiceEnabledFooterRow (lazy chunk
										containing @atlassian/ai-audio) or DisabledVoiceFooterRow (no
										audio deps), based on the consumer-supplied
										enableVoiceDictation prop. Both paths reproduce the
										existing footer-row layout exactly.
									*/}
									{(() => {
										// Slot variables shared between the lazy voice-enabled and
										// disabled fallback branches. Defined once to keep prop
										// updates (e.g. to ChatInputActionButtons or the submit
										// button) localized to a single source of truth.
										const promptControlsSlot = (
											<Inline alignBlock="center" space="space.050" grow="fill">
												{showActionsButton && (
													<ChatInputActionButtons
														menuItemsVisibility={actionsMenuItemsVisibility}
														entryPoint={entryPoint}
														siteId={siteId}
														placement={menuPosition}
														attachmentsAndCurrentPageAsContextStoreKey={promptDraftAndModeKey}
														isDisabled={isDisabled || isAgentTyping}
														onLinkTypeAheadClick={openLinkTypeAheadFromMenu}
														onMentionTypeAheadClick={openMentionTypeAhead}
														onAddTypeAheadClick={openAddTypeAhead}
														onDiscoverSkillsClick={onDiscoverSkillsClick}
														onDeepResearchClick={
															shouldShowDeepResearchInAddMenu ? handleDeepResearchClick : undefined
														}
														isDeepResearchActive={chatModeDeepResearchEnabled}
														onBrowsePromptLibraryClick={onBrowsePromptLibraryClick}
														onCreateSpaceClick={onCreateSpaceClick}
														hasSkillInPrompt={promptHasSkill}
														isAgentMentionActive={hasActiveAgentMention}
														assistanceServiceParams={assistanceServiceParams}
														fileUploadAssistanceOverrides={fileUploadAssistanceOverrides}
														stableFileInputChange={stableFileInputChange}
														contextProviders={contextProviders}
														onContextBrowserShow={onContextBrowserShow}
														onContextAvailable={onContextAvailable}
														useCurrentPageContext={useCurrentPageContext}
														onMenuOpen={handleMenuOpen}
														shouldRenderInPortal={shouldRenderQuickActionsInPortal}
														enableBrowserContextBanner={enableBrowserContextBanner}
														browserContextSanitizer={browserContextSanitizer}
													/>
												)}
												{showChatModeMenu &&
													(!hideRovoControlsForAgent || !!showAgentSourceControlSpotlight) && (
														<PillsWrapper chatModeKey={promptDraftAndModeKey}>
															{(hideLabel) => (
																<>
																	{(!hideRovoControlsForAgent ||
																		!!showAgentSourceControlSpotlight) && (
																		<FilteredAppsTag
																			assistanceServiceParams={assistanceServiceParams}
																			hideLabel={hideLabel}
																			chatModeKey={promptDraftAndModeKey}
																			contentContext={contentContext}
																			isDisabled={
																				isDisabled ||
																				isAgentTyping ||
																				hasActiveAgentMention ||
																				isSpecialistPromptInput
																			}
																			webSearchConfigEnabled={webSearchConfigEnabled}
																			onFilteredAppsChange={onFilteredAppsChange}
																			onWebSearchToggle={handleWebSearchToggle}
																			onOrgKnowledgeToggle={onOrgKnowledgeToggle}
																			enableAppsDiscoveryUi={enableAppsDiscoveryUi}
																			showSourceModeToggle={showSourceModeToggle}
																			shouldRenderInPortal={shouldRenderChatModeMenusInPortal}
																			disabledTooltip={
																				showDisabledRovoControlsForAgent
																					? filteredAppsAgentDisabledTooltip
																					: undefined
																			}
																			showAgentSourceControlSpotlight={
																				showAgentSourceControlSpotlight
																			}
																			onDismissAgentSourceControlSpotlight={
																				onDismissAgentSourceControlSpotlight
																			}
																			sourceControlPostOfficeMessage={
																				sourceControlPostOfficeMessage
																			}
																		/>
																	)}
																</>
															)}
														</PillsWrapper>
													)}
											</Inline>
										);
										const actionsBeforeSubmitSlot = (
											<>
												{customSlotBeforeChatModeMenu}
												{showChatModeMenu && !hideRovoControlsForAgent && (
													<ReasoningModeMenu
														chatModeState={chatModeStateForReasoningModeMenu}
														enableMaxMode={enableMaxMode}
														onReasoningModeChange={handleFastReasoningModeChange}
														renderAutoApplyToggle={renderAutoApplyToggle}
														isDisabled={
															isDisabled ||
															isAgentTyping ||
															hasActiveAgentMention ||
															isReasoningModeDisabledByDeepResearch ||
															isSpecialistPromptInput
														}
														disabledTooltip={
															isReasoningModeDisabledByDeepResearch
																? formatMessage(
																		reasoningModeMenuMessages.deepResearchAddMenuDisabledTooltip,
																	)
																: showDisabledRovoControlsForAgent
																	? reasoningModeAgentDisabledTooltip
																	: undefined
														}
														placement={menuPosition}
														shouldRenderInPortal={shouldRenderChatModeMenusInPortal}
														onMenuOpen={handleMenuOpen}
														onMenuOpenChange={onReasoningModeMenuOpenChange}
													/>
												)}
												{customSlotBeforeSubmitButton}
											</>
										);
										const submitButtonSlot = SubmitButton ? (
											<SubmitButton
												isAgentTyping={isAgentTyping}
												isSendDisabled={isSendDisabled}
												promptDraftAndModeKey={promptDraftAndModeKey}
												submitButtonTooltip={submitButtonTooltip}
												tooltipOptions={tooltipOptions}
												onSave={submitButtonOnSave}
												onAbort={onAbort}
												getChatMode={getChatMode}
											/>
										) : (
											<ControlPanel
												isAgentTyping={isAgentTyping}
												isSendDisabled={isSendDisabled}
												promptDraftAndModeKey={promptDraftAndModeKey}
												submitButtonTooltip={submitButtonTooltip}
												tooltipOptions={tooltipOptions}
												onSave={submitButtonOnSave}
												onAbort={onAbort}
												getChatMode={getChatMode}
											/>
										);
										const disabledFallback = (
											<DisabledVoiceFooterRow
												promptControlsSlot={promptControlsSlot}
												actionsBeforeSubmitSlot={actionsBeforeSubmitSlot}
												submitButtonSlot={submitButtonSlot}
											/>
										);
										return shouldRenderVoiceEnabledFooterRow ? (
											<ErrorBoundary
												fallbackRender={() => disabledFallback}
												onError={(error) => {
													logException({
														exception: error,
														name: 'load-panel-voice-enabled-footer-row',
														tags: {
															siteId,
														},
													});
												}}
											>
												<Suspense fallback={disabledFallback}>
													<VoiceEnabledFooterRow
														cloudId={siteId}
														entryPoint={entryPoint}
														mode={mode}
														productKey={productKey}
														productHeader={assistanceServiceParams.product}
														promptControlsSlot={promptControlsSlot}
														actionsBeforeSubmitSlot={actionsBeforeSubmitSlot}
														submitButtonSlot={submitButtonSlot}
														onInsertTranscript={onInsertTranscript}
														onMicPermissionDenied={onMicPermissionDenied}
														onDictationStatusChange={handleDictationStatusChange}
													/>
												</Suspense>
											</ErrorBoundary>
										) : (
											disabledFallback
										);
									})()}
								</Inline>
								{dropzoneContent}
								{autoSuggestConfig && (
									<UFOSuspense fallback={null} interactionName="chat-prompt-input-autosuggest">
										<AutoSuggest
											query={autoSuggestQuery}
											adf={autoSuggestADF ?? undefined}
											onSuggestionSelect={handleSuggestionSelect}
											position={autoSuggestConfig.position}
											cloudId={siteId}
											source={autoSuggestConfig.source}
											pageContextType={pageContextType}
											productKey={productKey}
										/>
									</UFOSuspense>
								)}
							</Stack>
						</Stack>
					</TaskModePromptAnimation>
				)}
			</FileUploadDropzone>
		</UFOSegment>
	);
};

const MemoizedChatInputInnerContent = React.memo(ChatInputInnerContent, shallowEqualObjects);

const StabilizedChatInputInner = ({
	editorApi,
	editorProps,
	...contentProps
}: Omit<ChatInputInnerProps, 'isRenderStabilizationEnabled'>): React.JSX.Element => {
	const editorStore = useEditorStore(editorApi, editorProps);

	return <MemoizedChatInputInnerContent {...contentProps} editorStore={editorStore} />;
};

export const ChatInputInner = (props: ChatInputInnerProps): React.JSX.Element => {
	const { isRenderStabilizationEnabled, ...contentProps } = props;

	return isRenderStabilizationEnabled ? (
		<StabilizedChatInputInner {...contentProps} />
	) : (
		<ChatInputInnerContent {...contentProps} />
	);
};
